/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  handleKeypress thunk
 */

import type { AutocompleteApiResult } from '@zcode/bizos'
import { isNumber } from '@zcode/utils'
import type { Dispatch, SetStateAction } from 'react'
import type * as Api from '../../../../../shared/api'
import { POPULAR_SEARCHES } from '../../../../../shared/components/Autocomplete/AutocompleteResults/popularSearches'
import { trackExitClick } from '../../../../../shared/components/Autocomplete/module/thunks/analytics'
import { ARROW_DOWN, ARROW_UP, ESC, TAB } from '../../../../../shared/constants/keys'
import { type AutocompleteOrigin, MIN_CHARACTER_COUNT } from '../../constants'
import { getPreviousSearches } from '../../helpers/getPreviousSearches'

const handleKeypress = (
  e: React.KeyboardEvent<HTMLInputElement>,
  origin: AutocompleteOrigin | undefined,
  exclude: Parameters<(typeof Api)['autocompleteResults']['query']>[0]['exclude'],
  searchTerm: string,
  predictionResults: readonly AutocompleteApiResult[] | null,
  keystrokes: number,
  setHoveredIndex: Dispatch<SetStateAction<number>>,
  setIsFocused: Dispatch<SetStateAction<boolean>>,
  setKeystrokes: Dispatch<SetStateAction<number>>
) => {
  const key = e.which

  const prevSearches = getPreviousSearches(exclude)
  let maxIndex: number

  if (predictionResults?.length) {
    maxIndex = predictionResults.length - 1
  } else if (searchTerm.length >= MIN_CHARACTER_COUNT && !predictionResults?.length) {
    maxIndex = POPULAR_SEARCHES.length
  } else if (prevSearches?.length) {
    // Need to account for UseMyCurrentLocation component when prevSearches are being rendered
    maxIndex = prevSearches.length
  } else {
    maxIndex = POPULAR_SEARCHES.length
  }

  if (key === ARROW_UP) {
    e.stopPropagation()
    setHoveredIndex((hoveredIndex) =>
      hoveredIndex && hoveredIndex > 0 ? hoveredIndex - 1 : maxIndex
    )
  } else if (key === ARROW_DOWN) {
    e.stopPropagation()
    setHoveredIndex((hoveredIndex) =>
      hoveredIndex < maxIndex && isNumber(hoveredIndex) ? hoveredIndex + 1 : 0
    )
  } else if (key === ESC || key === TAB) {
    if (key === ESC) {
      e.preventDefault()
      setIsFocused(false)
    }
    setHoveredIndex(-1)
    trackExitClick({ origin, keystrokes, predictionResults, searchTerm, setKeystrokes })
  }
}

export default handleKeypress
